import { useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronDown, CircleHelp, CreditCard, LifeBuoy, LogOut, Route, User, Users } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { displayRole, displayRoleLabel, isAdmin, type DisplayRole } from '@/features/auth/api';
import { useLogout, useMe } from '@/features/auth/hooks';
import { billingLinkVisible } from '@/features/billing/visibility';
import { useOnboardingState } from '@/features/dashboard/hooks';
import { uploadsInFlight } from '@/features/vault/uploadManager';
import { setDatePrefs } from '@/lib/dates';
import { useTour } from '@/features/tour/useTour';
import { TokenUsagePill } from '@/features/usage/components/TokenUsagePill';
import { useVersion } from '@/features/version/hooks';
import { MobileNav } from './MobileNav';

/** Per-role color so OWNER reads as gold (founder), ADMIN as brand-navy,
 * EDITOR as muted-blue, VIEWER as gray. Matches the hero/navbar palette. */
const ROLE_COLOR: Record<DisplayRole, string> = {
  OWNER: 'text-[#f4c95d]',
  ADMIN: 'text-electric',
  EDITOR: 'text-[#9bb1d4]',
  VIEWER: 'text-[#aab6cc]',
  REVIEWER: 'text-[#9bb1d4]',
};

/**
 * Header — top brand bar (logo + tagline + user menu).
 *
 * Background uses the same navy gradient that lives inside the REGARA logo
 * PNG (a 135° wash from `--color-navbar-bg-1` → `--color-navbar-bg-2`) so
 * the logo's own rectangle blends straight into the bar instead of reading
 * as a card-on-card. The auth pages keep their own (darker) indigo
 * gradient — this navbar tone is intentionally lighter.
 *
 * Identity: ``useMe()`` (TanStack Query) fetches the signed-in user from
 * ``/api/auth/me`` once per session and caches for 5 min. Until the
 * request resolves, the placeholder em-dashes match the vanilla site's
 * pre-fetch state. After login the cache is invalidated so the strip
 * updates immediately without a page reload. Logout removes the cache.
 *
 * Dropdown: ports the legacy ``navbar.html`` user menu — header (name +
 * email), Profile & Settings → /profile, Sign Out → POST
 * /api/auth/logout. Powered by the shadcn dropdown-menu primitive, which
 * handles a11y (Esc to close, focus trap, role=menu) for us.
 */
export function Header() {
  const me = useMe();
  const user = me.data;
  // Header is mounted on every authed page and reads useMe — the single point
  // that feeds the current user's display prefs into the shared date formatters.
  useEffect(() => {
    setDatePrefs(user?.timezone, user?.date_format, user?.time_format);
  }, [user?.timezone, user?.date_format, user?.time_format]);
  const logout = useLogout();
  const navigate = useNavigate();
  const { requestStart } = useTour();
  // Build identity (git SHA + time). == the ECR image tag in deployed envs, so
  // you can confirm at a glance which build you're looking at. Renders only
  // when the (authed) fetch succeeds.
  const build = useVersion().data;
  // Team & invites is admin-only. Editors + viewers never see the link
  // (and ``GET /api/team/members`` is also admin-gated server-side, so
  // pasting the URL fails too — defense in depth).
  const showTeamLink = isAdmin(user);
  const onboarding = useOnboardingState();
  const showBillingLink = billingLinkVisible(user, onboarding);
  // Role badge rendered after the company line ("Regara, Inc. · ADMIN").
  // Null while ``useMe()`` is loading so we don't briefly flash "VIEWER".
  // ``role`` drives the colour tone; ``roleLabel`` is the text (spells out
  // "EDITOR & REVIEWER" for the combined set). Both are null together.
  const role = displayRole(user);
  const roleLabel = displayRoleLabel(user);

  const signOut = async (stoppedUploads: number) => {
    try {
      await logout.mutateAsync();
    } catch {
      // Even if the server logout fails (e.g. transient 5xx), drop the
      // cookie locally and send the user to /login — better than leaving
      // them stuck on a half-authed page.
    }
    // A full-page redirect, not a client navigation: the notice about stopped uploads has to
    // survive the reload the 401 guard triggers, so it rides on the URL and the login page
    // renders it. window.location also guarantees a clean reset of the signed-out session.
    window.location.href = stoppedUploads > 0 ? `/login?uploads_stopped=${stoppedUploads}` : '/login';
  };

  // Sign out RIGHT AWAY, even mid-upload. This machine is the shared clinical workstation the
  // sign-out work exists for: a dialog that waits for "Sign out anyway" leaves a live session on
  // screen when the person walks off — the exact thing to avoid. The upload can't survive the
  // session either way (its bytes come from this browser), so there is nothing to keep by asking;
  // logout's cancelAllUploads stops the transfers, and the login page names what was stopped.
  const handleSignOut = () => {
    void signOut(uploadsInFlight());
  };

  return (
    <header
      className="border-b border-black/15"
      style={{
        background:
          'linear-gradient(135deg, var(--color-navbar-bg-1) 0%, var(--color-navbar-bg-2) 100%)',
      }}
    >
      {/* Full-width brand bar (the sidebar starts BELOW it) — standard
          content gutter on both sides. */}
      <div className="px-4 sm:px-6 lg:px-10 py-[18px] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-[22px] min-w-0">
          {/* Below lg the sidebar rail is hidden — the hamburger drawer is
              the ONLY section navigation on mobile. */}
          <MobileNav />
          {/* Brand zone. On lg+ the sidebar (w-60 = 240px) runs below this bar;
              with the header gutter at lg:px-10 (2.5rem), a fixed 12.5rem brand
              zone puts its right border EXACTLY on the sidebar's right edge
              (2.5 + 12.5 = 15rem = 240px). Below lg the sidebar is hidden, so
              the zone is auto-width and the inline md divider below handles the
              logo/tagline separation instead. */}
          <div className="flex items-center lg:w-[12.5rem] lg:h-9 lg:border-r lg:border-white/20">
            <a
              href="/dashboard"
              aria-label="Go to dashboard"
              className="inline-flex items-center no-underline leading-none transition-opacity duration-150 hover:opacity-85 focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-4 rounded"
            >
              {/* Letters-only wordmark (transparent, trimmed) — sits directly
                  on the navbar gradient instead of bringing its own rectangle. */}
              <img src="/regara_logo_1.png" alt="REGARA" className="h-6 sm:h-8 w-auto block" />
            </a>
          </div>
          {/* md-only divider: at md the sidebar is hidden (nothing to align to),
              so keep the original inline rule between logo and tagline. */}
          <div className="hidden md:block lg:hidden w-px h-9 bg-white/20" />
          <div className="hidden md:block font-sans text-[13px] tracking-[2.4px] text-[#aab6cc] font-medium">
            CLINICAL &amp; REGULATORY PLATFORM
          </div>
        </div>

        <div className="flex items-center gap-3.5">
          <TokenUsagePill />
          <div className="hidden sm:block text-right leading-tight">
            <div className="font-bold text-white text-[14px]">
              {user?.name || '—'}
            </div>
            <div className="text-[#aab6cc] text-[13px]">
              {user?.company || user?.email || '—'}
              {role && (
                <>
                  {' · '}
                  <span
                    className={`font-semibold tracking-[0.06em] ${ROLE_COLOR[role]}`}
                  >
                    {roleLabel}
                  </span>
                </>
              )}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-tour="account-menu"
                aria-label={user ? `Open user menu — ${user.name}` : 'User menu'}
                aria-haspopup="menu"
                className="
                  group inline-flex items-center gap-1.5 bg-transparent cursor-pointer
                  rounded-full p-0.5
                  hover:opacity-85
                  focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-2
                  transition-opacity
                "
              >
                <div
                  className="
                    size-9 rounded-full
                    bg-gradient-to-br from-[#6B8AFF] to-[#8B6BFF]
                    text-white font-bold text-[14px] tracking-wide
                    inline-flex items-center justify-center
                  "
                >
                  {avatarInitial(user)}
                </div>
                <ChevronDown
                  className="size-4 text-[#aab6cc] mr-1 transition-transform duration-150 group-data-[state=open]:rotate-180"
                  aria-hidden="true"
                />
              </button>
            </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="min-w-[240px]">
            <DropdownMenuLabel className="font-normal">
              <div className="font-semibold text-navy text-[13px]">
                {user?.name || 'Loading…'}
              </div>
              <div className="text-[12px] text-muted truncate">
                {user?.email || ''}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile">
                <User className="size-4" /> Profile &amp; Settings
              </Link>
            </DropdownMenuItem>
            {showTeamLink && (
              <DropdownMenuItem asChild>
                <Link to="/team">
                  <Users className="size-4" /> Team &amp; invites
                </Link>
              </DropdownMenuItem>
            )}
            {showBillingLink && (
              <DropdownMenuItem asChild>
                <Link to="/billing">
                  <CreditCard className="size-4" /> Billing
                </Link>
              </DropdownMenuItem>
            )}
            {/* FAQ above Help & Support on purpose — self-serve answers
                first, contacting the team second (the support page itself
                says "Check the FAQ first"). */}
            <DropdownMenuItem asChild>
              <Link to="/faq">
                <CircleHelp className="size-4" /> FAQ
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/support">
                <LifeBuoy className="size-4" /> Help &amp; Support
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                requestStart('dashboard');
                void navigate({ to: '/dashboard' });
              }}
            >
              <Route className="size-4" /> Take the tour
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onSelect={handleSignOut}
            >
              <LogOut className="size-4" /> Sign Out
            </DropdownMenuItem>
            {build && (
              <>
                <DropdownMenuSeparator />
                <div className="select-text px-2 py-1.5 font-mono text-[11px] leading-tight text-muted">
                  {build.sha} · {build.build_time}
                </div>
              </>
            )}
          </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

/**
 * One-character avatar glyph. First letter of ``name`` when populated,
 * first letter of ``email`` as a fallback, em-dash while loading.
 */
function avatarInitial(user: { name?: string; email?: string } | null | undefined): string {
  const source = (user?.name ?? user?.email ?? '').trim();
  if (!source) return '—';
  return source[0]!.toUpperCase();
}
